import React, { useEffect, useMemo, useState } from "react";
import { getPublicDemo } from "../lib/api";
import {
  Sprout, ArrowRight, ArrowLeft, ShoppingCart, Store, Cpu, Smartphone, WashingMachine,
  Wheat, TrendingDown, AlertTriangle, DollarSign, Percent, Layers, Building2, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { useSEO } from "../lib/useSEO";

const INDUSTRIES = [
  { key: "grocery", label: "Grocery", icon: ShoppingCart },
  { key: "retail", label: "Retail", icon: Store },
  { key: "electronics", label: "Electronics", icon: Cpu },
  { key: "cellular", label: "Cellular", icon: Smartphone },
  { key: "appliances", label: "Appliances", icon: WashingMachine },
  { key: "agriculture", label: "Agriculture", icon: Wheat },
];

const money = (n) => {
  const v = n || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `$${Math.round(v / 1000)}K`;
  return `$${Math.round(v)}`;
};
const moneyFull = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const sevCls = {
  high: "bg-destructive/15 text-red-300 border-red-500/30",
  medium: "bg-[#F59E0B]/15 text-amber-300 border-amber-500/30",
  low: "bg-white/10 text-white/60 border-white/15",
};

export default function DemoPreview({ initialVertical, onGetStarted, onLogin, onHome }) {
  const valid = INDUSTRIES.some((i) => i.key === initialVertical);
  const [vertical, setVertical] = useState(valid ? initialVertical : "grocery");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getPublicDemo(vertical).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    if (typeof window !== "undefined") window.history.replaceState({}, "", `/demo/${vertical}`);
  }, [vertical]);

  const terms = data?.terms || {};
  const catLabel = terms.categories || "Categories";
  const label = INDUSTRIES.find((i) => i.key === vertical)?.label || "Industry";
  useSEO({
    title: `AssortIQ ${label} Demo — AI Assortment Optimization Dashboard`,
    description: `Explore a live ${label.toLowerCase()} assortment dashboard: KPIs, category performance, revenue trends and competitor price gaps — powered by explainable AI. No signup required.`,
    canonical: `https://getassortiq.com/demo/${vertical}`,
  });
  const trend = useMemo(() => (data?.trend || []).map((t) => ({ ...t, w: (t.week || "").slice(5) })), [data]);
  const maxCatRev = Math.max(1, ...(data?.top_categories || []).map((c) => c.revenue_total));

  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased overflow-x-hidden">
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-forest-900/85 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <button data-testid="demo-home" onClick={onHome} className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-5 w-5" strokeWidth={2.4} /></div>
            <span className="font-heading font-extrabold text-xl">Assort<span className="text-ai">IQ</span></span>
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="demo-back" onClick={onHome} className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Home
            </button>
            <button data-testid="demo-login" onClick={onLogin} className="text-sm font-semibold text-white/80 hover:text-white">Log in</button>
            <button data-testid="demo-nav-cta" onClick={onGetStarted} className="group flex items-center gap-1.5 rounded-full bg-ai px-4 py-2 text-sm font-bold hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
              Start free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-ai/40 bg-ai/10 px-3 py-1 text-xs font-semibold text-ai uppercase tracking-wider">
          <Sparkles className="h-3.5 w-3.5" /> Live demo · real sample data
        </div>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold leading-tight">Explore a live dashboard</h1>
        <p className="mt-3 text-white/70 max-w-2xl">Pick an industry to preview exactly what AssortIQ shows your team — KPIs, category performance, revenue trends and competitor price gaps. No signup required.</p>

        {/* SWITCHER */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="demo-switcher">
          {INDUSTRIES.map((ind) => {
            const active = ind.key === vertical;
            return (
              <button
                key={ind.key}
                data-testid={`demo-tab-${ind.key}`}
                onClick={() => setVertical(ind.key)}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                  active ? "bg-ai text-white" : "border border-white/15 text-white/70 hover:bg-white/10"
                }`}
              >
                <ind.icon className="h-4 w-4" /> {ind.label}
              </button>
            );
          })}
        </div>

        {loading || !data ? (
          <div className="py-24 flex justify-center">
            <div className="h-10 w-10 rounded-full border-2 border-ai border-t-transparent animate-spin" />
          </div>
        ) : (
          <div className="mt-8 space-y-6 animate-fade-up" data-testid="demo-dashboard">
            <div className="text-sm text-white/50">Previewing <span className="text-white font-semibold">{data.name}</span> — {data.summary.total_products} products · {data.summary.total_stores} locations · {data.summary.weeks_covered} weeks</div>

            {/* KPI CARDS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: DollarSign, label: "Total revenue", value: money(data.summary.total_revenue), sub: `${money(data.summary.total_weekly_revenue)}/wk` },
                { icon: Percent, label: "Avg margin", value: `${data.summary.avg_margin_pct}%`, sub: `${data.summary.avg_stockout_pct}% stockout` },
                { icon: Layers, label: catLabel, value: data.summary.total_categories, sub: `${data.summary.total_products} ${terms.skus || "products"}` },
                { icon: Building2, label: terms.stores || "Locations", value: data.summary.total_stores, sub: `${data.summary.weeks_covered} weeks` },
              ].map((k) => (
                <div key={k.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <k.icon className="h-5 w-5 text-ai" />
                  <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-white/45">{k.label}</div>
                  <div className="mt-1 font-heading text-3xl font-extrabold">{k.value}</div>
                  <div className="mt-0.5 text-xs text-white/45">{k.sub}</div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* TREND */}
              <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <h3 className="font-heading text-lg font-bold">Weekly revenue trend</h3>
                <div className="mt-4 h-64 w-full" style={{ minHeight: 256 }}>
                  <ResponsiveContainer width="100%" height="100%" minHeight={256}>
                    <AreaChart data={trend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                      <defs>
                        <linearGradient id="demoFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#FF5E00" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#FF5E00" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="w" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis tickFormatter={money} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={false} width={44} domain={["dataMin - 30000", "dataMax + 20000"]} />
                      <Tooltip contentStyle={{ background: "#0B1510", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, color: "#fff", fontSize: 12 }} formatter={(v) => [moneyFull(v), "Revenue"]} />
                      <Area type="monotone" dataKey="revenue" stroke="#FF5E00" strokeWidth={2.4} fill="url(#demoFill)" isAnimationActive={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* TOP CATEGORIES */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <h3 className="font-heading text-lg font-bold">Top {catLabel.toLowerCase()}</h3>
                <div className="mt-4 space-y-3" data-testid="demo-top-categories">
                  {data.top_categories.map((c) => (
                    <div key={c.category}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-semibold truncate">{c.category}</span>
                        <span className="font-mono text-white/70">{money(c.revenue_total)}</span>
                      </div>
                      <div className="mt-1 h-1.5 w-full rounded-full bg-white/10">
                        <div className="h-full rounded-full bg-ai" style={{ width: `${Math.max(4, (c.revenue_total / maxCatRev) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* PRICE GAPS */}
            {data.price_gaps.length > 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="demo-price-gaps">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  <h3 className="font-heading text-lg font-bold">Price gaps to act on</h3>
                </div>
                <p className="text-sm text-white/50 mt-1">{catLabel} where a rival is undercutting your shelf price the most.</p>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {data.price_gaps.map((g) => (
                    <div key={g.category} className="rounded-xl border border-white/10 p-4">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold truncate">{g.category}</span>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${sevCls[g.severity]}`}>{g.severity}</span>
                      </div>
                      <div className="mt-2 flex items-baseline gap-1.5">
                        <TrendingDown className="h-4 w-4 text-destructive" />
                        <span className="font-heading text-xl font-extrabold text-red-300">-{g.undercut_pct}%</span>
                      </div>
                      <div className="mt-1 text-xs text-white/50">vs {g.competitor}</div>
                      <div className="mt-2 text-sm font-mono text-red-300">{money(g.revenue_at_risk)}/wk at risk</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="relative overflow-hidden rounded-3xl border border-ai/30 bg-gradient-to-br from-ai/20 to-transparent p-8 sm:p-10 text-center">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,94,0,0.15),transparent_70%)]" />
              <div className="relative">
                <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Run this on your own data</h2>
                <p className="mt-2 text-white/70 max-w-xl mx-auto">Upload a CSV or connect your POS and see your real numbers in the first session — free for 7 days.</p>
                <button data-testid="demo-footer-cta" onClick={onGetStarted} className="mt-6 inline-flex items-center gap-2 rounded-full bg-ai px-7 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
                  Start free trial <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className="text-center text-xs text-white/40">Demo uses illustrative sample data for {data.name}. Competitive figures are simulated.</p>
          </div>
        )}
      </div>
    </div>
  );
}
